import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { isNative, setTokens, clearTokens } from "@/lib/native";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Guards against the mount-time /auth/me (fired before cookies exist) resolving
  // late and clobbering a user set by an in-flight login/register.
  const authedRef = useRef(false);

  // Bootstrap session: web via cookie, native via stored token (attached by api interceptor).
  // Skip the 401 refresh-retry here: a fresh visitor has no session, so a 401 simply means
  // "not logged in" and must resolve loading immediately (never block public pages on it).
  useEffect(() => {
    let settled = false;
    const finish = () => { if (!settled) { settled = true; setLoading(false); } };
    api
      .get("/auth/me", { skipAuthRefresh: true })
      .then((res) => { if (!authedRef.current) setUser(res.data); })
      .catch(() => { if (!authedRef.current) setUser(null); })
      .finally(finish);
    // Failsafe: never leave the app stuck on the splash if the request stalls.
    const t = setTimeout(finish, 6000);
    return () => clearTimeout(t);
  }, []);

  // When silent refresh fails (refresh token expired/invalid), end the session.
  useEffect(() => {
    const onExpired = () => { authedRef.current = false; setUser(null); };
    window.addEventListener("auth:expired", onExpired);
    return () => window.removeEventListener("auth:expired", onExpired);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (isNative()) await setTokens(data.token, data.refresh_token);
    authedRef.current = true;
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    if (isNative()) await setTokens(data.token, data.refresh_token);
    authedRef.current = true;
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.error("Logout request failed:", e);
    }
    if (isNative()) await clearTokens();
    authedRef.current = false;
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/profile");
      authedRef.current = true;
      setUser(data);
      return data;
    } catch (e) {
      console.error("Failed to refresh user:", e);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}
